import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SvgIconComponent, injectRegisterIcons } from '@ngneat/svg-icon';
import { TranslatePipe } from '@ngx-translate/core';

import { RouteSegment } from '@core/enums/route-segment.enum';
import { TenantsStore } from '@core/stores/tenants/tenants.store';

import { AlertingTableSortPropertyStoreModelEnum } from '@shared/enums/alerting-table-sort-property.enum';
import { SortDirectionStoreModelEnum } from '@shared/enums/sort-direction.enum';
import { PopoverDirective } from '@shared/modules/popover';
import { TranslateAlertSeverityPipe } from '@shared/pipes/translate-enum-pipes/translate-alert-severity.pipe';
import { TranslateAlertTypePipe } from '@shared/pipes/translate-enum-pipes/translate-alert-type.pipe';
import { getAlertSeverityTableStatusCellProperties } from '@shared/utils/alert-severity-status-cell-properties';

import { appInfo16Icon } from '@icons/info-16';

import { ButtonComponent } from '../button';
import { LoadingSkeletonComponent } from '../loading-skeleton';
import { SiteDateTimeComponent } from '../site-date-time';
import { TableComponent } from '../table';
import { TableStatusCellComponent } from '../table-status-cell';
import { TableViewLinkComponent } from '../table-view-link';
import { TableCellSortableHeaderComponent } from '../table/components/table-cell-sortable-header';
import { TableCellHeaderDirective } from '../table/directives/table-cell-header.directive';
import { TableCellDirective } from '../table/directives/table-cell.directive';
import {
  AlertingCardTableActionClickEvent,
  AlertingCardTableDataSourceViewModel,
  AlertingCardTableSortChangeEvent,
  AlertingCardTableViewModel,
} from './alerting-card-table.view-model';
import { getTypeAsAlertType } from './helpers/get-type-as-alert-type';

@Component({
  selector: 'app-alerting-card-table',
  imports: [
    TableComponent,
    TableCellSortableHeaderComponent,
    TranslatePipe,
    TableCellDirective,
    TableCellHeaderDirective,
    LoadingSkeletonComponent,
    RouterLink,
    TableViewLinkComponent,
    TableStatusCellComponent,
    TranslateAlertTypePipe,
    PopoverDirective,
    NgClass,
    ButtonComponent,
    SvgIconComponent,
    SiteDateTimeComponent,
  ],
  providers: [TranslateAlertSeverityPipe],
  templateUrl: './alerting-card-table.component.html',
  styleUrl: './alerting-card-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertingCardTableComponent {
  readonly #translateAlertSeverityPipe = inject(TranslateAlertSeverityPipe);
  public readonly tenantsStore = inject(TenantsStore);

  public readonly vm = input.required<AlertingCardTableViewModel>();
  public readonly emptyText = input<string>();
  public readonly sopColumnIsHidden = input<boolean>(false);
  public readonly createdAtRightAligned = input<boolean>(false);
  public readonly severityColumnIsShown = input<boolean>(false);
  public readonly dismissedReasonColumnIsShown = input<boolean>(false);
  public readonly actionsColumnIsShown = input<boolean>(false);
  public readonly actionButtonLabel = input<string>();
  public readonly sortChange = output<AlertingCardTableSortChangeEvent>();
  public readonly actionClick = output<AlertingCardTableActionClickEvent>();

  public readonly tableStatusCellProperties = getAlertSeverityTableStatusCellProperties(this.#translateAlertSeverityPipe);

  public readonly RouteSegment = RouteSegment;
  public readonly AlertingTableSortPropertyStoreModelEnum = AlertingTableSortPropertyStoreModelEnum;
  public readonly getTypeAsAlertType = getTypeAsAlertType;

  public loadingSkeletonRows = Array.from({ length: 5 }).map(() => ({})) as AlertingCardTableDataSourceViewModel[];

  constructor() {
    injectRegisterIcons([appInfo16Icon]);
  }

  public onSortClick(sortProperty: AlertingTableSortPropertyStoreModelEnum, sortDirection: SortDirectionStoreModelEnum): void {
    this.sortChange.emit({ sortProperty, sortDirection });
  }

  public onActionClick(data: AlertingCardTableDataSourceViewModel): void {
    this.actionClick.emit({ id: data.id, siteId: data.siteId });
  }
}
